import {CELL_OUTLINE_TEXTURE_KEYS} from '../../configs/fieldCellOutlines';
import {hexDistance} from './sizeRecruitmentTextures';

/**
 * Текстуры обводок ячеек (см. configs/fieldCellOutlines): шестиугольное кольцо по
 * контуру ячейки, белое, красится tint. Считается без подвыборок, сглаживание дает
 * расстояние до контура: кольца большие, а выбрать отряд должно быть мгновенно.
 */

export const CELL_OUTLINE_TEXTURE_SIZE = 256;
// Радиус вершин кольца на картинке: снаружи остается запас под свечение
export const CELL_OUTLINE_RADIUS_PX = 112;

const APOTHEM_PX = CELL_OUTLINE_RADIUS_PX * Math.cos(Math.PI / 6);
const THIN_HALF_WIDTH = 3;
const THICK_HALF_WIDTH = 7.5;
const GLOW_WIDTH = 17;
const VERTEX_SPARK_RADIUS = 16;
// Штрихов на половине стороны: от середины стороны к вершинам, поэтому на общей
// стороне двух ячеек штрихи совпадают
const DASHES_PER_HALF_SIDE = 2;
const DASH_FILL_SHARE = 0.62;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const buildPixels = (resolveAlpha) => {
  const size = CELL_OUTLINE_TEXTURE_SIZE;
  const pixels = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const offset = (y * size + x) * 4;
      const px = x + 0.5 - size / 2;
      const py = y + 0.5 - size / 2;
      pixels[offset] = 255;
      pixels[offset + 1] = 255;
      pixels[offset + 2] = 255;
      pixels[offset + 3] = Math.round(255 * clamp01(resolveAlpha(px, py, hexDistance(px, py, APOTHEM_PX))));
    }
  }
  return pixels;
};

const lineAlpha = (distance, halfWidth) => clamp01(halfWidth + 0.5 - Math.abs(distance));

// Доля стороны от ее середины: 0 — середина, 1 — вершина
const resolveAlongSide = (px, py) => {
  const sector = Math.round(Math.atan2(py, px) / (Math.PI / 3) - 0.5) + 0.5;
  const normal = sector * (Math.PI / 3);
  const along = -px * Math.sin(normal) + py * Math.cos(normal);
  return Math.abs(along) / (CELL_OUTLINE_RADIUS_PX / 2);
};

export const buildCellOutlineCorePixels = () => buildPixels((px, py, distance) => lineAlpha(distance, THIN_HALF_WIDTH));

export const buildCellOutlineThickCorePixels = () => buildPixels((px, py, distance) => lineAlpha(distance, THICK_HALF_WIDTH));

export const buildCellOutlineDashedCorePixels = () => buildPixels((px, py, distance) => {
  const dash = (resolveAlongSide(px, py) * DASHES_PER_HALF_SIDE + (1 - DASH_FILL_SHARE) / 2) % 1;
  return dash < DASH_FILL_SHARE ? lineAlpha(distance, THIN_HALF_WIDTH) : 0;
});

/** Свечение: мягко гаснет от контура в обе стороны, в вершинах ярче — искры */
export const buildCellOutlineGlowPixels = () => buildPixels((px, py, distance) => {
  const glow = clamp01(1 - Math.abs(distance) / GLOW_WIDTH) ** 1.6;
  const radius = Math.hypot(px, py);
  const nearVertexAngle = Math.round(Math.atan2(py, px) / (Math.PI / 3)) * (Math.PI / 3);
  const toVertex = Math.hypot(
    px - Math.cos(nearVertexAngle) * CELL_OUTLINE_RADIUS_PX,
    py - Math.sin(nearVertexAngle) * CELL_OUTLINE_RADIUS_PX,
  );
  const spark = radius > 0 ? clamp01(1 - toVertex / VERTEX_SPARK_RADIUS) ** 2 : 0;
  return 0.8 * glow + 0.6 * spark;
});

const PIXEL_BUILDERS = {
  [CELL_OUTLINE_TEXTURE_KEYS.CORE]: buildCellOutlineCorePixels,
  [CELL_OUTLINE_TEXTURE_KEYS.CORE_THICK]: buildCellOutlineThickCorePixels,
  [CELL_OUTLINE_TEXTURE_KEYS.CORE_DASHED]: buildCellOutlineDashedCorePixels,
  [CELL_OUTLINE_TEXTURE_KEYS.GLOW]: buildCellOutlineGlowPixels,
};

/**
 * Зарегистрировать текстуры один раз на сцену
 * @param {Phaser.Scene} scene
 * @param {Object} [options]
 * @param {number} [options.linearFilter] — режим фильтрации LINEAR
 * @returns {boolean} готовы ли текстуры
 */
export const ensureCellOutlineTextures = (scene, {linearFilter} = {}) => {
  const textures = scene?.textures;
  if (!textures?.exists || !textures.addUint8Array) {
    return false;
  }

  return Object.values(CELL_OUTLINE_TEXTURE_KEYS).every((key) => {
    if (textures.exists(key)) {
      return true;
    }
    const size = CELL_OUTLINE_TEXTURE_SIZE;
    const texture = textures.addUint8Array(key, PIXEL_BUILDERS[key](), size, size);
    if (!texture) {
      return false;
    }
    if (linearFilter !== undefined) {
      texture.setFilter?.(linearFilter);
    }
    return true;
  });
};
